import { Badge, Box } from "@chakra-ui/react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { IFilterQuotation, TableQuotationProps } from "./interface";
import {
  PaginatorInfo,
  Quotation,
  QuotationStatus,
  useListQuotationsQuery,
} from "../../generated/graphql";
import { createColumnHelper } from "@tanstack/react-table";
import Filter from "./Filter";
import update from "immutability-helper";
import DefaultTable, {
  TableImperativeHandle,
  ExtendedDefaultTableProps,
} from "../DefaultTable";
import NumberFormat from "../NumberFormat";
import TextCopy from "../TextCopy";
import moment from "moment";
import {
  getQuotationStatusColorScheme,
  getQuotationStatusName,
} from "../../helpers/global";
import { getThisYear } from "../DateRangePicker/data";

const columnHelper = createColumnHelper<Quotation>();

const TableQuotation = forwardRef<
  TableImperativeHandle<Quotation>,
  ExtendedDefaultTableProps<Quotation, IFilterQuotation> & TableQuotationProps
>(
  (
    {
      filterParams,
      onFilterSubmit,
      page,
      onPageChange,
      onDelete,
      status,
      ...props
    },
    ref
  ) => {
    useImperativeHandle(ref, () => ({
      updateRow(data) {
        setDatas((datas) => {
          const index = datas.findIndex((elm) => elm.id === data.id);
          return update(datas, {
            [index]: {
              $set: { ...datas[index], ...data },
            },
          });
        });
      },
      refresh() {
        refetch();
      },
    }));

    const [localFilterParams, setLocalFilterParams] =
      useState<IFilterQuotation>();
    const [localPage, setLocalPage] = useState<number>(1);
    const [datas, setDatas] = useState<Quotation[]>([]);

    const columns = useMemo(() => {
      return [
        columnHelper.accessor("bookingCode", {
          header: () => "Booking Code",
          cell: (info) => (
            <TextCopy textCopy={info.getValue()}>
              <Box as="span">{info.getValue()}</Box>
            </TextCopy>
          ),
        }),
        columnHelper.accessor("itinerary", {
          header: () => "Itinerary",
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor("to", {
          header: () => "To",
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor("itineraryDate", {
          header: () => "Itinerary Date",
          cell: (info) => moment(info.getValue()).format("D/MMM/YYYY"),
        }),
        columnHelper.accessor("status", {
          header: () => "Status",
          cell: (info) => (
            <Badge
              colorScheme={getQuotationStatusColorScheme(
                info.getValue() as QuotationStatus
              )}
            >
              {getQuotationStatusName(info.getValue() as QuotationStatus)}
            </Badge>
          ),
        }),
        columnHelper.accessor("confirmedAt", {
          header: () => "Confirmed At",
          cell: (info) =>
            info.getValue()
              ? moment(info.getValue()).format("D/MMM/YYYY")
              : "-",
        }),
        columnHelper.accessor("subTotal", {
          header: "Subtotal",
          cell: (info) => <NumberFormat prefix="$ " value={info.getValue()} />,
        }),
        columnHelper.accessor("additionalCost", {
          header: "Eddition Costa",
          cell: (info) => <NumberFormat prefix="$ " value={info.getValue()} />,
        }),
        columnHelper.accessor("total", {
          header: "NTA",
          cell: (info) => <NumberFormat prefix="$ " value={info.getValue()} />,
        }),
      ];
    }, []);

    useEffect(() => {
      if (filterParams !== undefined) setLocalFilterParams(filterParams);
    }, [filterParams]);

    useEffect(() => {
      if (page !== undefined) setLocalPage(page);
    }, [page]);

    useEffect(() => {
      setLocalFilterParams({ ...localFilterParams, status });
    }, [status]);

    const [paginator, setPaginator] = useState<PaginatorInfo>();

    const thisMonth = getThisYear();

    const { data, loading, refetch } = useListQuotationsQuery({
      notifyOnNetworkStatusChange: true,
      variables: {
        params: {
          key: localFilterParams?.key || undefined,
          status: localFilterParams?.status || undefined,
        },
      },
    });

    const [isLazyLoading, setIsLazyLoading] = useState(true);

    useEffect(() => {
      setPaginator(data?.listQuotations.paginatorInfo);
      setDatas((data?.listQuotations.data || []) as Quotation[]);
    }, [data]);

    useEffect(() => {
      setIsLazyLoading(loading);
    }, [loading, setIsLazyLoading]);

    useEffect(() => {
      refetch({
        page: localPage,
        params: {
          key: localFilterParams?.key || undefined,
          status: localFilterParams?.status || undefined,
          dateRange: localFilterParams?.dateRange
            ? {
                from: moment(localFilterParams?.dateRange[0]).format("Y-MM-DD"),
                to: moment(localFilterParams?.dateRange[1]).format("Y-MM-DD"),
              }
            : {
                from: moment(thisMonth[0]).format("Y-MM-DD"),
                to: moment(thisMonth[1]).format("Y-MM-DD"),
              },
        },
      });
    }, [localFilterParams, localPage, refetch]);

    return (
      <DefaultTable
        data={datas}
        columns={columns}
        isLoading={loading || isLazyLoading}
        getRowId={(row) => row.id}
        filterComponent={
          <Filter
            value={localFilterParams}
            onSubmit={(data) => {
              setLocalPage(1);
              setLocalFilterParams(data);
              onFilterSubmit && onFilterSubmit(data);
            }}
          />
        }
        paginator={paginator}
        onPageChange={(page) => {
          setLocalPage(page);
          onPageChange && onPageChange(page);
        }}
        {...props}
      />
    );
  }
);

export default TableQuotation;
